import { WAIVER_MATRIX, WaiverGround, waiverRuleFor } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Badge, Input, Label, RadioGroup, Radio } from '@fluxora/ui';

/**
 * The PUCT §25.478 deposit-waiver matrix (E-SF05 AC1) as FIRST-CLASS RULES.
 *
 * Each ground is a selectable radio — age 62+, family-violence certification,
 * medical indigence, the low-income two-installment split, and the guarantor
 * option (a visibly-badged STUB). This is AC1's requirement made literal: not a
 * free-text "reason" box, but distinct rules the customer elects, each carrying
 * its own effect and certification requirement. The canonical list comes from the
 * contract's `WAIVER_MATRIX`, so the screen and the BFF agree on the grounds.
 */

export interface WaiverMatrixProps {
  /** The grounds offered by the decision (the full §25.478 set). */
  readonly offeredWaivers: readonly WaiverGround[];
  readonly selected: WaiverGround | undefined;
  readonly onSelect: (ground: WaiverGround) => void;
  readonly certificationRef: string;
  readonly onCertificationRefChange: (value: string) => void;
}

/** Certification kinds that need a reference the customer supplies. */
function needsReference(certification: string): boolean {
  return (
    certification === 'FAMILY_VIOLENCE_CERTIFICATION' ||
    certification === 'MEDICAL_STATEMENT' ||
    certification === 'LOW_INCOME_PROOF'
  );
}

export function WaiverMatrix({
  offeredWaivers,
  selected,
  onSelect,
  certificationRef,
  onCertificationRefChange,
}: WaiverMatrixProps): JSX.Element {
  const { t } = useTranslation();

  const rules = WAIVER_MATRIX.filter((r) => offeredWaivers.includes(r.ground));
  const selectedRule = selected === undefined ? undefined : waiverRuleFor(selected);

  const handleChange = (value: string): void => {
    const parsed = WaiverGround.safeParse(value);
    if (parsed.success) onSelect(parsed.data);
  };

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium text-foreground">{t('shop.deposit.waiver.title')}</h3>
      <p className="text-xs text-foreground/70">{t('shop.deposit.waiver.intro')}</p>

      <RadioGroup value={selected} onChange={handleChange} ariaLabel={t('shop.deposit.waiver.title')}>
        {rules.map((rule) => (
          <Radio
            key={rule.ground}
            value={rule.ground}
            label={
              <span className="flex items-center gap-2">
                {t(`shop.deposit.waiver.${rule.i18nKey}.label`)}
                {rule.isStub ? (
                  <Badge variant="warning">{t('shop.deposit.waiver.stub.badge')}</Badge>
                ) : null}
              </span>
            }
            description={
              <span className="flex flex-col">
                <span>{t(`shop.deposit.waiver.${rule.i18nKey}.desc`)}</span>
                <span className="text-[11px] text-foreground/50">{rule.citation}</span>
              </span>
            }
          />
        ))}
      </RadioGroup>

      {selectedRule !== undefined && needsReference(selectedRule.certification) ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor="waiver-cert-ref">{t('shop.deposit.waiver.certRef.label')}</Label>
          <Input
            id="waiver-cert-ref"
            name="certificationRef"
            value={certificationRef}
            onChange={(e) => onCertificationRefChange(e.target.value)}
            placeholder={t('shop.deposit.waiver.certRef.placeholder')}
          />
          <span className="text-[11px] text-foreground/50">{t('shop.deposit.waiver.certRef.note')}</span>
        </div>
      ) : null}
    </div>
  );
}
